/* Loaded only by the opt-in Beginner Set template. */
(() => {
  const makeItems = (kitId, boxId, planId, requestId, packageChoice, monthlyScent) => [
    { id: Number(kitId), quantity: 1, properties: { _monian_bundle: requestId, ...(packageChoice ? { Package: packageChoice } : {}) } },
    { id: Number(boxId), quantity: 1, selling_plan: Number(planId), properties: { _monian_bundle: requestId, ...(monthlyScent ? { 'Monthly scent': monthlyScent } : {}) } }
  ];
  const ownLines = (cart, requestId) => cart.items.filter(item => item.properties?._monian_bundle === requestId);
  const assertSubscriptionDiscounts = (cart, allowedCode) => {
    const allowed = String(allowedCode || '').trim().toLowerCase();
    const other = (cart.discount_codes || []).some(item => String(item.code || '').trim().toLowerCase() !== allowed);
    if (other) throw new Error('Additional discount codes cannot be used with this subscription. Please remove the other code from your cart before continuing.');
  };
  const completePair = (cart, requestId, kitId, boxId, planId) => {
    const lines = ownLines(cart, requestId);
    return lines.length === 2 && lines.every(item => item.quantity === 1) &&
      lines.some(item => item.variant_id === Number(kitId) && !item.selling_plan_allocation) &&
      lines.some(item => item.variant_id === Number(boxId) && item.selling_plan_allocation?.selling_plan.id === Number(planId));
  };
  const validateIntro = (cart, requestId, config) => {
    if (cart.currency !== 'USD' || !completePair(cart, requestId, config.kitId, config.boxId, config.planId)) return false;
    const lines = ownLines(cart, requestId);
    const kit = lines.find(item => item.variant_id === Number(config.kitId));
    const box = lines.find(item => item.variant_id === Number(config.boxId));
    return kit.final_line_price === config.kitPrice && box.final_line_price === 0;
  };
  // Pure helpers are exported for Node tests; no browser libraries are needed.
  if (typeof module !== 'undefined') module.exports = { makeItems, ownLines, completePair, validateIntro, assertSubscriptionDiscounts };
  if (typeof customElements === 'undefined' || customElements.get('monian-dual-purchase')) return;

  class MonianDualPurchase extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      this.scope = document.getElementById(`shopify-section-${this.config.sectionId}`);
      this.form = document.getElementById(`product-form-${this.config.sectionId}`);
      if (!this.form || !this.scope) return;
      const policy = this.querySelector('.monian-purchase__policy');
      if (policy) this.form.after(policy);
      this.error = this.querySelector('[data-error]');
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.addEventListener('change', () => this.update(), options);
      // Capture before the theme's product-form handler; one-time remains native.
      this.scope.addEventListener('submit', event => {
        if (event.target !== this.form || this.mode !== 'subscription') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!this.busy) this.addBundle();
      }, { capture: true, ...options });
      this.update();
      this.hidden = false;
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.scope?.classList.remove('monian-subscription-selected');
      this.initialized = false;
    }
    update() {
      this.mode = this.querySelector('[data-purchase-choice]:checked')?.value || 'once';
      this.scope.classList.toggle('monian-subscription-selected', this.mode === 'subscription');
      this.querySelector('[data-renewal]').hidden = this.mode !== 'subscription';
      const scents = this.querySelector('[data-scent-controls]');
      if (scents) scents.hidden = this.mode !== 'subscription';
      const specific = this.querySelector('[data-scent-mode]:checked')?.value === 'specific';
      const picker = this.querySelector('[data-scent-picker]');
      if (picker) picker.hidden = !specific;
      const submit = this.form.querySelector('[type="submit"]');
      if (submit) {
        this.originalSubmitDisabled ??= submit.disabled;
        submit.disabled = this.originalSubmitDisabled || (this.mode === 'subscription' && !this.config.ready);
      }
      this.error.textContent = '';
    }
    selectedMonthly() {
      if (!this.config.monthlyVariants) return this.config;
      const specific = this.querySelector('[data-scent-mode]:checked')?.value === 'specific';
      const value = specific ? this.querySelector('[data-scent-variant]')?.value : 'Mysterious scent';
      const selected = this.config.monthlyVariants.find(item => item.title === value);
      if (!selected) return { ...this.config, boxId: 0, planId: 0, ready: false };
      return { ...this.config, boxId: selected.id, planId: selected.planId, monthlyScent: selected.title,
        ready: this.config.ready && selected.available && selected.price === 4000 };
    }
    async request(path, body) {
      const response = await fetch(`${window.Shopify.routes.root}${path}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { method: 'POST', body: JSON.stringify(body) } : { cache: 'no-store' })
      });
      const value = await response.json();
      if (!response.ok) throw new Error(typeof value.description === 'string' ? value.description : 'Your cart could not be updated.');
      return value;
    }
    async addBundle() {
      const c = this.selectedMonthly();
      if (!c.ready || !c.discountCode || !c.boxId || !c.planId) {
        this.showError('This monthly subscription is not available yet. Please choose a one-time purchase.');
        return;
      }
      const idInput = this.form.elements.namedItem('id');
      const packageChoice = this.querySelector('[data-kit-package]')?.value;
      if (Number(idInput?.value) !== Number(c.kitId)) {
        this.showError('Your product selection has changed. Please refresh the page and try again.');
        return;
      }
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      const submit = this.form.querySelector('[type="submit"]');
      const wasDisabled = submit?.disabled;
      if (submit) submit.disabled = true;
      this.querySelectorAll('input').forEach(input => input.disabled = true);
      try {
        let cart = await this.request('cart.js');
        if (cart.currency !== 'USD') throw new Error('This subscription is currently available only in USD.');
        assertSubscriptionDiscounts(cart, c.discountCode);
        if (!this.requestId && cart.items.some(item => (c.monthlyVariants || [{id: c.boxId}]).some(variant => Number(variant.id) === item.variant_id) && item.selling_plan_allocation)) {
          throw new Error('Your cart already contains a monthly subscription. Please review it before adding another.');
        }
        this.requestId ||= `monian-${crypto.randomUUID()}`;
        const lines = ownLines(cart, this.requestId);
        if (!lines.length) {
          try {
            await this.request('cart/add.js', { items: makeItems(c.kitId, c.boxId, c.planId, this.requestId, packageChoice, c.monthlyScent) });
          } catch (error) {
            // A timeout does not prove the write failed. Read back before any retry.
            cart = await this.request('cart.js');
            if (!completePair(cart, this.requestId, c.kitId, c.boxId, c.planId)) throw error;
          }
        } else if (!completePair(cart, this.requestId, c.kitId, c.boxId, c.planId)) {
          throw new Error('Your cart contains an incomplete bundle. Please review it before trying again.');
        }
        cart = await this.request('cart.js');
        if (!completePair(cart, this.requestId, c.kitId, c.boxId, c.planId)) throw new Error('We could not confirm that the complete bundle was added to your cart.');
        // Do not erase unrelated discounts silently; reject them before applying the approved introductory code.
        assertSubscriptionDiscounts(cart, c.discountCode);
        cart = await this.request('cart/update.js', { discount: c.discountCode });
        assertSubscriptionDiscounts(cart, c.discountCode);
        if (!validateIntro(cart, this.requestId, c)) {
          throw new Error('We could not verify your first-payment offer. Please review your cart before checking out.');
        }
        window.location.assign(`${window.Shopify.routes.root}cart`);
      } catch (error) {
        this.showError(error.message || 'The connection was interrupted. Please review your cart before trying again to avoid duplicate items.');
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
        if (submit) submit.disabled = wasDisabled;
        this.querySelectorAll('input').forEach(input => input.disabled = false);
      }
    }
    showError(message) {
      this.error.textContent = message;
      this.error.focus();
      this.querySelector('[data-cart-link]').hidden = false;
    }
  }
  customElements.define('monian-dual-purchase', MonianDualPurchase);
})();
